import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { PassportScoreCard } from "../components/PassportScoreCard";
import { Shield, Upload, FileText, CheckCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const SECTION_META = [
  { key: "profile", label: "Personal Details", icon: FileText },
  { key: "dbs", label: "DBS", icon: Shield },
  { key: "rtw", label: "Right To Work", icon: Shield },
  { key: "agreement", label: "Self-Employed Agreement", icon: FileText },
  { key: "training", label: "Training", icon: CheckCircle },
  { key: "campaigns", label: "Campaign Eligibility", icon: Shield },
];

export default function FundraiserPassportCentre() {
  const navigate = useNavigate();
  const u = JSON.parse(localStorage.getItem("user") || "{}");
  const [active, setActive] = useState("profile");
  const [profile, setProfile] = useState({ address: "", phone: "", utrNumber: "",
                                              emergencyContactName: "", emergencyContactPhone: "" });
  const [me, setMe] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const r = await apiClient.get(`/auth/me`);
      setMe(r.data);
      setProfile(p => ({
        ...p,
        address: r.data.address || "",
        phone: r.data.phone || r.data.phoneNumber || "",
        utrNumber: r.data.utrNumber || "",
        emergencyContactName: r.data.emergencyContactName || "",
        emergencyContactPhone: r.data.emergencyContactPhone || "",
      }));
    } catch (e) { /* ignore */ }
    try {
      const r = await apiClient.get(`/workforce/users/${u.id}/agreements`);
      setAgreements(r.data || []);
    } catch (e) { /* ignore */ }
    try {
      const r = await apiClient.get(`/training`);
      setTrainings(r.data || []);
    } catch (e) { /* ignore */ }
    try {
      const r = await apiClient.get(`/campaigns`);
      setCampaigns(r.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // ─── Action handlers ───
  const saveProfile = async () => {
    setBusy(true);
    try {
      await apiClient.put(`/team/${u.id}/details`, profile);
      toast.success("Profile saved");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const uploadDoc = async (file, hint) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await apiClient.post(`/uploads/document`, fd);
      toast.success(`${hint} uploaded`);
      return r.data?.url || r.data?.documentUrl || null;
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || "error"}`);
      return null;
    }
  };

  const submitDbs = async (file) => {
    const url = await uploadDoc(file, "DBS");
    if (url) {
      await apiClient.put(`/team/${u.id}/details`, { dbsDocumentUrl: url });
      reload();
    }
  };

  const submitRtw = async (file) => {
    const url = await uploadDoc(file, "RTW");
    if (url) {
      await apiClient.put(`/team/${u.id}/details`, { rtwDocumentUrl: url });
      toast.info("RTW evidence submitted. Awaiting owner verification.");
      reload();
    }
  };

  const signAgreement = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/workforce/agreements/sign`, {
        userId: u.id, version: "v1.0",
        signedName: me?.name || u.name,
        documentReference: "agreement-v1.0.pdf",
      });
      toast.success("Agreement signed");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign failed");
    } finally { setBusy(false); }
  };

  const ackTraining = async (id) => {
    try {
      await apiClient.post(`/training/${id}/acknowledge`);
      toast.success("Training acknowledged");
      reload();
    } catch (e) {
      toast.error("Acknowledge failed");
    }
  };

  const activeAgreement = (agreements || []).find(a => a.status === "signed");
  const outstandingTraining = (trainings || []).filter(
    t => t.requiresAcknowledgement && !(t.acknowledgedBy || []).includes(u.id)
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="passport-centre">
      <div className="max-w-5xl mx-auto p-4 md:p-8">
        <Button variant="ghost" onClick={() => navigate("/fundraiser/dashboard")}
                className="mb-4" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to dashboard
        </Button>
        <h1 className="text-3xl font-heading font-bold mb-6 flex items-center gap-2">
          <Shield className="h-7 w-7 text-slate-700" /> Fundraiser Passport Centre
        </h1>

        {/* Live score */}
        <div className="mb-6">
          <PassportScoreCard userId={u.id} campaignId={null} />
        </div>

        {/* Section tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="section-tabs">
          {SECTION_META.map(s => (
            <button key={s.key} onClick={() => setActive(s.key)}
              data-testid={`section-tab-${s.key}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                active === s.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white border-slate-200 text-slate-700 hover:bg-slate-100"
              }`}>
              <s.icon className="inline h-3.5 w-3.5 mr-1" /> {s.label}
            </button>
          ))}
        </div>

        {/* Section content */}
        <Card className="border-slate-200">
          <CardContent className="p-6">

            {active === "profile" && (
              <div className="space-y-3" data-testid="section-profile">
                <h3 className="font-semibold mb-2">Personal details</h3>
                <div>
                  <Label>Email</Label>
                  <Input value={me?.email || u.email || ""} disabled />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={profile.phone}
                    onChange={e => setProfile({ ...profile, phone: e.target.value })}
                    data-testid="profile-phone" />
                </div>
                <div>
                  <Label>Address</Label>
                  <Textarea value={profile.address}
                    onChange={e => setProfile({ ...profile, address: e.target.value })}
                    data-testid="profile-address" />
                </div>
                <div>
                  <Label>Emergency contact name</Label>
                  <Input value={profile.emergencyContactName}
                    onChange={e => setProfile({ ...profile, emergencyContactName: e.target.value })}
                    data-testid="profile-emergency-name" />
                </div>
                <div>
                  <Label>Emergency contact phone</Label>
                  <Input value={profile.emergencyContactPhone}
                    onChange={e => setProfile({ ...profile, emergencyContactPhone: e.target.value })}
                    data-testid="profile-emergency-phone" />
                </div>
                <div>
                  <Label>UTR Number (self-employed)</Label>
                  <Input value={profile.utrNumber}
                    onChange={e => setProfile({ ...profile, utrNumber: e.target.value })}
                    data-testid="profile-utr" />
                </div>
                <Button onClick={saveProfile} disabled={busy}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="profile-save-btn">Save profile</Button>
              </div>
            )}

            {active === "dbs" && (
              <div className="space-y-3" data-testid="section-dbs">
                <h3 className="font-semibold">DBS check</h3>
                <p className="text-sm text-slate-600">
                  Upload your DBS certificate (PDF/JPG/PNG). Owner will verify after submission.
                </p>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Status: {me?.compliance?.dbsChecked ? "Verified" : "Pending"}</Badge>
                  {me?.compliance?.dbsCheckDate && (
                    <Badge variant="outline">Checked: {String(me.compliance.dbsCheckDate).slice(0, 10)}</Badge>
                  )}
                </div>
                <Input type="file" accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => submitDbs(e.target.files?.[0])}
                  data-testid="dbs-upload-input" />
              </div>
            )}

            {active === "rtw" && (
              <div className="space-y-3" data-testid="section-rtw">
                <h3 className="font-semibold">Right To Work evidence</h3>
                <p className="text-sm text-slate-600">
                  Upload passport / share-code letter. Owner will mark as Verified.
                </p>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Status: {me?.rightToWorkStatus || "pending"}</Badge>
                  {me?.rightToWorkExpiry && (
                    <Badge variant="outline">Expires: {String(me.rightToWorkExpiry).slice(0, 10)}</Badge>
                  )}
                </div>
                <Input type="file" accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => submitRtw(e.target.files?.[0])}
                  data-testid="rtw-upload-input" />
              </div>
            )}

            {active === "agreement" && (
              <div className="space-y-3" data-testid="section-agreement">
                <h3 className="font-semibold">Self-Employed Agreement</h3>
                <p className="text-sm text-slate-600">
                  By signing this agreement, you confirm your engagement as a self-employed
                  fundraiser with this organisation.
                </p>
                {activeAgreement ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4"
                    data-testid="agreement-signed-card">
                    <div className="font-medium text-emerald-800 mb-1">
                      ✓ Agreement signed
                    </div>
                    <div className="text-sm text-slate-700">
                      Version {activeAgreement.version} · signed{" "}
                      {String(activeAgreement.signedAt || "").slice(0, 10)}
                    </div>
                  </div>
                ) : (
                  <Button onClick={signAgreement} disabled={busy}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="agreement-sign-btn">
                    Sign agreement
                  </Button>
                )}
              </div>
            )}

            {active === "training" && (
              <div className="space-y-3" data-testid="section-training">
                <h3 className="font-semibold">Training</h3>
                {outstandingTraining.length === 0 ? (
                  <div className="text-emerald-700 text-sm">
                    All required training acknowledged ✓
                  </div>
                ) : (
                  outstandingTraining.map(t => (
                    <div key={t.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3"
                      data-testid={`training-item-${t.id}`}>
                      <div>
                        <div className="font-medium">{t.title}</div>
                        <div className="text-xs text-slate-500">{t.description}</div>
                      </div>
                      <Button size="sm" onClick={() => ackTraining(t.id)}
                        data-testid={`training-ack-${t.id}`}>
                        Acknowledge
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}

            {active === "campaigns" && (
              <div className="space-y-3" data-testid="section-campaigns">
                <h3 className="font-semibold">Campaign Eligibility</h3>
                {campaigns.length === 0 ? (
                  <div className="text-sm text-slate-500">No campaigns visible yet.</div>
                ) : (
                  campaigns.map(c => (
                    <div key={c.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-100"
                      data-testid={`campaign-eligibility-${c.id}`}>
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-slate-500">
                          {c.startDate ? `From ${String(c.startDate).slice(0, 10)}` : ""}
                        </div>
                      </div>
                      <Badge variant="outline">
                        {(me?.campaignIds || []).includes(c.id) ? "Assigned" : "Eligible (request)"}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            )}

          </CardContent>
        </Card>
      </div>
    </div>
  );
}
